import { useEffect, useMemo, useRef } from 'react';
import { useFieldHelper, LinkFieldProps, LinkField } from 'framework/ui/field';
import { getAnchor, scrollToHash } from 'framework/utils';
import { useLocation } from 'react-router-dom';
import { ScheduleItemStatus } from '../../billingTypes';
import {
  ACCESS_INFO_SECTIONS,
  ACCESS_INFO_ACTIONS,
  useProjectSecurityContext,
  useNewProjectUrl,
} from 'domain/shared/newProject';

const PreviewBillingRequestLinkField = ({
  id,
  billingScheduleIdSource,
  billingRequestIdSource,
  statusSource,
  ...props
}: LinkFieldProps & {
  projectId: string;
}) => {
  const { getValue: getAlias } = useFieldHelper(props);
  const { getValue: getBillingScheduleId } = useFieldHelper({ source: billingScheduleIdSource });
  const billingScheduleId = getBillingScheduleId();
  const anchor = useMemo(() => getAnchor(getAlias()), [getAlias]);
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const { hash } = useLocation();
  const { getApproveBillingRequestUrl, getPreviewBillingRequestUrl } = useNewProjectUrl();
  const { getValue: getBillingRequestId } = useFieldHelper({ source: billingRequestIdSource });
  const billingRequestId = getBillingRequestId();
  const { getValue: getStatus } = useFieldHelper({ source: statusSource });
  const status = getStatus();
  const { isAccessGranted } = useProjectSecurityContext();
  const hasReviewPermission = isAccessGranted(ACCESS_INFO_SECTIONS.BILLING_SCHEDULE, ACCESS_INFO_ACTIONS.REVIEW);
  const hasManagePermission = isAccessGranted(ACCESS_INFO_SECTIONS.BILLING_SCHEDULE, ACCESS_INFO_ACTIONS.MANAGE);

  useEffect(() => {
    if (hash && anchorRef.current && anchorRef.current.getAttribute('href') === hash) {
      scrollToHash(anchorRef.current);
    }
  }, [hash]);

  const getBillingRequestUrl = () => {
    if (!billingRequestId) {
      return null;
    }

    if (status === ScheduleItemStatus.IN_APPROVAL || status === ScheduleItemStatus.PLANNED) {
      if (hasReviewPermission) {
        return getApproveBillingRequestUrl({ billingScheduleId, billingRequestId });
      }
      if (hasManagePermission) {
        return getPreviewBillingRequestUrl({ billingScheduleId, billingRequestId });
      }
    }

    if (
      (status === ScheduleItemStatus.DRAFT ||
        status === ScheduleItemStatus.CLARIFICATION_REQUESTED ||
        status === ScheduleItemStatus.APPROVED) &&
      hasManagePermission
    ) {
      return getPreviewBillingRequestUrl({ billingScheduleId, billingRequestId });
    }

    return null;
  };

  const billingRequestUrl = getBillingRequestUrl();

  return (
    <a href={`#${anchor}`} id={`copy-${anchor}`} ref={anchorRef} className="invisible-anchor">
      {billingRequestUrl ? (
        <LinkField linkText={getAlias()} isInternal value={billingRequestUrl} />
      ) : (
        <span>{getAlias()}</span>
      )}
    </a>
  );
};
export default PreviewBillingRequestLinkField;
